import { initializer, inject, injectable } from "@launchtray/tsyringe-async";
import { PromiseCompletionSource } from "async/PromiseCompletionSource";
import { ICanvasScaler } from "by/gamefactory/canvas/ICanvasScaler";
import { ILogger } from "by/gamefactory/cityversetycoon/logger/ILogger";
import { Viewable } from "by/gamefactory/scene/viewable/Viewable";
import { AsyncOperationNotification } from "../../../common/components/AsyncOperationNotification";
import { IBenefitsModel } from "../../../common/models/benefits/IBenefitsModel";
import { CommonResourcesPackage } from "../../../common/resources/CommonResourcesPackage";
import { NoCacheDynamicPackage } from "../../../common/resources/NoCacheDynamicPackage";
import { DiscountResourcePackage } from "./resources/DiscountResourcePackage";
import { MyDiscountPopupView } from "./view/MyDiscountPopupView";
import { DynamicResourcesPackage } from "resources/DynamicResourcesPackage";
import { CancellationToken } from "async/CancellationToken";

@injectable()
export class MyDiscountPopupScene extends Viewable<void> {
    private static readonly NO_CACHE_TAG = "my_discount";

    private readonly commonResources: CommonResourcesPackage;
    private readonly canvasScaler: ICanvasScaler;
    private readonly logger: ILogger;
    private readonly benefitModel: IBenefitsModel;
    private readonly asyncOperationNotification: AsyncOperationNotification;
    private readonly discountResources: DiscountResourcePackage;
    private readonly cancellationToken: CancellationToken;

    private benefitsDynamicResources: DynamicResourcesPackage;

    public constructor(
        commonResources: CommonResourcesPackage,
        @inject("ICanvasScaler") canvasScaler: ICanvasScaler,
        @inject("ILogger") logger: ILogger,
        @inject("IBenefitsModel") benefitModel: IBenefitsModel,
        asyncOperationNotification: AsyncOperationNotification,
        cancellationToken: CancellationToken
    ) {
        super();
        this.commonResources = commonResources;
        this.canvasScaler = canvasScaler;
        this.logger = logger;
        this.benefitModel = benefitModel;
        this.asyncOperationNotification = asyncOperationNotification;
        this.cancellationToken = cancellationToken;
        this.discountResources = new DiscountResourcePackage();
    }

    @initializer()
    public async initialization(): Promise<void> {
        this.asyncOperationNotification.show();
        try {
            await this.discountResources.load();
            await this.benefitModel.getBenefits();
            this.benefitsDynamicResources = await this.loadDynamicResources();
            this.asyncOperationNotification.hide();
            const pcs = new PromiseCompletionSource<void>();
            this.view = new MyDiscountPopupView(
                pcs,
                this.cancellationToken,
                this.commonResources,
                this.benefitsDynamicResources,
                this.discountResources,
                this.canvasScaler,
                this.benefitModel
            );
            pcs.result.then(() => {
                this.discountResources.destroy();
                if (this.benefitsDynamicResources.loaded) {
                    this.benefitsDynamicResources.destroy();
                }
                this.operationControls.complete();
            }, (reason: unknown) => {
                this.discountResources.destroy();
                if (this.benefitsDynamicResources.loaded) {
                    this.benefitsDynamicResources.destroy();
                }
                this.operationControls.error(reason);
            });

            if (this.cancellationToken.isCancellationRequested) {
                this.discountResources.destroy();
                if (this.benefitsDynamicResources.loaded) {
                    this.benefitsDynamicResources.destroy();
                }
                this.operationControls.complete();
                return;
            }
        }
        catch (e: unknown) {
            this.logger.error(e);
            if (this.benefitsDynamicResources?.loaded) {
                this.benefitsDynamicResources.destroy();
            }
            if (this.discountResources.loaded) {
                this.discountResources.destroy();
            }
            this.asyncOperationNotification.hide();
            this.operationControls.error(e);
        }
    }

    private async loadDynamicResources(): Promise<DynamicResourcesPackage> {
        const benefitsDynamicResources = new NoCacheDynamicPackage(MyDiscountPopupScene.NO_CACHE_TAG);
        const benefits = this.benefitModel.benefits.value;
        if (benefits == null || benefits.length === 0) {
            return benefitsDynamicResources;
        }

        for (const benefit of benefits) {
            benefitsDynamicResources.add(benefit.imageUrl);
        }

        return await benefitsDynamicResources.load();
    }
}
